import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout, { canAccessPath } from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Inquiry from "@/pages/Inquiry";
import InquiryRequests from "@/pages/InquiryRequests";
import InquiryReports from "@/pages/InquiryReports";
import NonOrganicOrders from "@/pages/NonOrganicOrders";
import Cart from "@/pages/Cart";
import { QuotationsList, QuotationDetail } from "@/pages/Quotations";
import QuotationPrint from "@/pages/QuotationPrint";
import SalesOrderPrint from "@/pages/SalesOrderPrint";
import SalesOrders, { SalesOrderDetail } from "@/pages/SalesOrders";
import PickAndPack from "@/pages/PickAndPack";
import { Picklists, Deliveries, Returns } from "@/pages/Logistics";
import Approvals from "@/pages/Approvals";
import { Customers, Products } from "@/pages/MasterData";
import {
    UsersAdmin, RolesAdmin, EmailSetup, EmailTemplates, SAPConnection,
    SyncMonitor, PostingQueue, ErrorLogs, AuditLogs, ApproverAssignments,
} from "@/pages/Admin";
import { SchemaManagement, UserAssignments, WarehouseManagement } from "@/pages/SchemaAdmin";
import ProjectRegistrations from "./pages/ProjectRegistrations";
import Reports from "@/pages/Reports";
import CrmDashboard from "@/pages/crm/CrmDashboard";
import Leads from "@/pages/crm/Leads";
import ManualSales from "@/pages/crm/ManualSales";
import Pipeline from "@/pages/crm/Pipeline";
import DealDetail from "@/pages/crm/DealDetail";
import { AccountsList, AccountDetail } from "@/pages/crm/Accounts";
import Contacts from "@/pages/crm/Contacts";
import MyActivity from "@/pages/crm/MyActivity";
import TeamActivity from "@/pages/crm/TeamActivity";
import Outreach from "@/pages/crm/Outreach";
import AgentStats from "@/pages/crm/AgentStats";
import CrmAnalytics from "@/pages/crm/CrmAnalytics";
import SalesQuota from "@/pages/crm/SalesQuota";
import CrmManagerAssignments from "@/pages/crm/CrmManagerAssignments";
import StoreMapping from "@/pages/crm/StoreMapping";
import PriceGroups from "@/pages/PriceGroups";

function Protected({ children }) {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
                Loading…
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

// Per-route access guard: redirects non-admins away from admin-only views if they
// deep-link the URL directly (the sidebar already hides these). Uses the same
// MODULE_ACCESS rules as the nav via canAccessPath.
function RequireAccess({ path, children }) {
    const { user, hasPermission } = useAuth();
    if (user === null) return null; // Protected wrapper renders the loader
    if (!canAccessPath(path, user, hasPermission)) return <Navigate to="/dashboard" replace />;
    return children;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/quotations/:id/print" element={<Protected><QuotationPrint /></Protected>} />
                        <Route path="/sales-orders/:id/print" element={<Protected><SalesOrderPrint /></Protected>} />
                        <Route element={<Protected><AppLayout /></Protected>}>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/inquiry" element={<Inquiry />} />
                            <Route path="/inquiry-requests" element={<RequireAccess path="/inquiry-requests"><InquiryRequests /></RequireAccess>} />
                            <Route path="/admin/inquiry-reports" element={<RequireAccess path="/admin/inquiry-reports"><InquiryReports /></RequireAccess>} />
                            <Route path="/non-organic/orders" element={<RequireAccess path="/non-organic/orders"><NonOrganicOrders /></RequireAccess>} />
                            <Route path="/cart" element={<Cart />} />
                            <Route path="/project-registrations" element={<RequireAccess path="/project-registrations"><ProjectRegistrations /></RequireAccess>} />
                            <Route path="/reports" element={<RequireAccess path="/reports"><Reports /></RequireAccess>} />
                            <Route path="/quotations" element={<QuotationsList />} />
                            <Route path="/quotations/:id" element={<QuotationDetail />} />
                            <Route path="/sales-orders" element={<RequireAccess path="/sales-orders"><SalesOrders /></RequireAccess>} />
                            <Route path="/treasury/deposits" element={<RequireAccess path="/treasury/deposits"><SalesOrders depositMode /></RequireAccess>} />
                            <Route path="/sales-orders/:id" element={<SalesOrderDetail />} />
                            <Route path="/pick-and-pack" element={<RequireAccess path="/pick-and-pack"><PickAndPack /></RequireAccess>} />
                            <Route path="/picklists" element={<Picklists />} />
                            <Route path="/deliveries" element={<Deliveries />} />
                            <Route path="/returns" element={<Returns />} />
                            <Route path="/approvals" element={<Approvals />} />
                            <Route path="/customers" element={<Customers />} />
                            <Route path="/products" element={<Products />} />
                            <Route path="/crm" element={<RequireAccess path="/crm"><CrmDashboard /></RequireAccess>} />
                            <Route path="/crm/leads" element={<Leads />} />
                            <Route path="/crm/sales" element={<RequireAccess path="/crm/sales"><ManualSales /></RequireAccess>} />
                            <Route path="/crm/pipeline" element={<Pipeline />} />
                            <Route path="/crm/deals/:id" element={<DealDetail />} />
                            <Route path="/crm/accounts" element={<RequireAccess path="/crm/accounts"><AccountsList /></RequireAccess>} />
                            <Route path="/crm/accounts/:id" element={<RequireAccess path="/crm/accounts"><AccountDetail /></RequireAccess>} />
                            <Route path="/crm/contacts" element={<RequireAccess path="/crm/contacts"><Contacts /></RequireAccess>} />
                            <Route path="/crm/activities" element={<MyActivity />} />
                            <Route path="/crm/outreach" element={<RequireAccess path="/crm/outreach"><Outreach /></RequireAccess>} />
                            <Route path="/crm/team" element={<RequireAccess path="/crm/team"><TeamActivity /></RequireAccess>} />
                            <Route path="/crm/agent-stats" element={<RequireAccess path="/crm/agent-stats"><AgentStats /></RequireAccess>} />
                            <Route path="/crm/analytics" element={<RequireAccess path="/crm/analytics"><CrmAnalytics /></RequireAccess>} />
                            <Route path="/crm/quota" element={<RequireAccess path="/crm/quota"><SalesQuota /></RequireAccess>} />
                            <Route path="/admin/crm/managers" element={<CrmManagerAssignments />} />
                            <Route path="/admin/crm/store-map" element={<RequireAccess path="/admin/crm/store-map"><StoreMapping /></RequireAccess>} />
                            <Route path="/admin/users" element={<UsersAdmin />} />
                            <Route path="/admin/roles" element={<RolesAdmin />} />
                            <Route path="/admin/schemas" element={<SchemaManagement />} />
                            <Route path="/admin/user-assignments" element={<UserAssignments />} />
                            <Route path="/admin/price-groups" element={<RequireAccess path="/admin/price-groups"><PriceGroups /></RequireAccess>} />
                            <Route path="/admin/approver-assignments" element={<ApproverAssignments />} />
                            <Route path="/admin/warehouses" element={<WarehouseManagement />} />
                            <Route path="/admin/email-setup" element={<EmailSetup />} />
                            <Route path="/admin/email-templates" element={<EmailTemplates />} />
                            <Route path="/admin/sap-connection" element={<SAPConnection />} />
                            <Route path="/admin/sync-monitor" element={<SyncMonitor />} />
                            <Route path="/admin/posting-queue" element={<PostingQueue />} />
                            <Route path="/admin/error-logs" element={<ErrorLogs />} />
                            <Route path="/admin/audit-logs" element={<AuditLogs />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster richColors position="top-right" />
            </AuthProvider>
        </div>
    );
}

export default App;
